import type {
  AnnualFlightPlan,
  AnnualPlanStatus,
  AnnualPlanTarget,
  ApprovalStep,
  FlightPurpose,
  ValidationIssue,
} from '@/shared/types'

const PURPOSE_LABEL: Record<FlightPurpose, string> = {
  training: 'Huấn luyện bay',
  'combat-readiness': 'Trực sẵn sàng chiến đấu',
  transport: 'Vận chuyển',
  reconnaissance: 'Trinh sát',
  sar: 'Tìm kiếm cứu nạn',
  patrol: 'Tuần tra',
  test: 'Bay thử kỹ thuật',
}

function buildApprovalChain(status: AnnualPlanStatus): ApprovalStep[] {
  const levels: ApprovalStep['level'][] = ['trung-doan', 'su-doan', 'quan-chung']
  const stopAt = (() => {
    switch (status) {
      case 'draft':
      case 'submitted':
        return -1
      case 'pending-su-doan':
        return 0
      case 'pending-quan-chung':
        return 1
      case 'approved':
      case 'in-execution':
      case 'completed':
        return 2
      case 'rejected':
        return 1
      case 'cancelled':
        return 0
      default:
        return -1
    }
  })()
  return levels.map((level, idx) => {
    let stepStatus: ApprovalStep['status'] = 'pending'
    if (idx <= stopAt) stepStatus = 'approved'
    if (status === 'rejected' && idx === stopAt) stepStatus = 'rejected'
    return {
      level,
      approverName: ['Đại tá Trần Đức Tuấn', 'Đại tá Lê Quang Minh', 'Thiếu tướng Phạm Hoàng Sơn'][idx],
      approverRank: ['Đại tá', 'Đại tá', 'Thiếu tướng'][idx],
      status: stepStatus,
      timestamp: idx <= stopAt ? `2025-1${idx + 1}-${10 + idx * 5}T09:00:00` : undefined,
      comment:
        stepStatus === 'rejected'
          ? 'Cần điều chỉnh lại định mức nhiên liệu Q3 và bổ sung phương án dự bị máy bay.'
          : stepStatus === 'approved'
            ? 'Đồng ý phê duyệt.'
            : undefined,
    }
  })
}

interface PlanSeed {
  id: string
  unitId: string
  unitShort: string
  status: AnnualPlanStatus
  title: string
  description: string
  preparedAt: string
  approvedAt?: string
  aircraftAssigned: number
  pilotAssigned: number
  totalBudgetM: number
  targets: { purpose: FlightPurpose; sorties: number; hours: number; completed: number }[]
  notes?: string
  validationIssues: ValidationIssue[]
}

const SEEDS: PlanSeed[] = [
  {
    id: 'ap-2026-921',
    unitId: 'u-trd-921',
    unitShort: '921',
    status: 'in-execution',
    title: 'Kế hoạch bay năm 2026 — Trung đoàn KQ 921',
    description:
      'Kế hoạch tổng thể hoạt động bay năm 2026 của Trung đoàn không quân 921 (Su-30MK2): huấn luyện chuyển loại, sẵn sàng chiến đấu Bắc Bộ, tuần tra Vịnh Bắc Bộ.',
    preparedAt: '2025-09-15',
    approvedAt: '2025-12-22',
    aircraftAssigned: 18,
    pilotAssigned: 42,
    totalBudgetM: 86400,
    targets: [
      { purpose: 'training', sorties: 480, hours: 720, completed: 184 },
      { purpose: 'combat-readiness', sorties: 240, hours: 360, completed: 92 },
      { purpose: 'patrol', sorties: 120, hours: 220, completed: 48 },
      { purpose: 'test', sorties: 36, hours: 54, completed: 14 },
    ],
    notes: 'Phối hợp với Sư đoàn 371 trong các đợt tập trận tháng 4, 8, 11.',
    validationIssues: [
      { level: 'info', category: 'budget', message: 'Ngân sách Q1 đã sử dụng 28% — đúng tiến độ', detail: '24,200 / 86,400 triệu VND' },
      { level: 'warning', category: 'pilot', message: '3 phi công Cấp 1 sắp đến hạn khám sức khoẻ định kỳ trong T6', detail: 'Cần lên lịch khám trước 15/05/2026' },
    ],
  },
  {
    id: 'ap-2026-923',
    unitId: 'u-trd-923',
    unitShort: '923',
    status: 'in-execution',
    title: 'Kế hoạch bay năm 2026 — Trung đoàn KQ 923',
    description:
      'Kế hoạch hoạt động bay năm 2026 của Trung đoàn 923 (Su-22M4): huấn luyện cường kích, trực sẵn sàng Trung Trung Bộ, hỗ trợ tác chiến mặt đất.',
    preparedAt: '2025-09-10',
    approvedAt: '2025-12-20',
    aircraftAssigned: 14,
    pilotAssigned: 32,
    totalBudgetM: 64800,
    targets: [
      { purpose: 'training', sorties: 360, hours: 540, completed: 142 },
      { purpose: 'combat-readiness', sorties: 180, hours: 270, completed: 68 },
      { purpose: 'reconnaissance', sorties: 60, hours: 110, completed: 22 },
    ],
    validationIssues: [
      { level: 'warning', category: 'aircraft', message: '2 máy bay Su-22M4 đến hạn đại tu trong Q2', detail: '7803-04 và 7805-02 — đã lên kế hoạch chuyển sang Bộ tư lệnh KQ' },
    ],
  },
  {
    id: 'ap-2026-918',
    unitId: 'u-trd-918',
    unitShort: '918',
    status: 'in-execution',
    title: 'Kế hoạch bay năm 2026 — Lữ đoàn KQ 918',
    description:
      'Kế hoạch vận tải hàng không quân sự năm 2026 của Lữ đoàn 918 (C-295, Mi-17): vận chuyển nhân sự, hậu cần, SAR, hỗ trợ thiên tai.',
    preparedAt: '2025-08-25',
    approvedAt: '2025-12-15',
    aircraftAssigned: 12,
    pilotAssigned: 28,
    totalBudgetM: 52000,
    targets: [
      { purpose: 'transport', sorties: 320, hours: 480, completed: 138 },
      { purpose: 'sar', sorties: 80, hours: 120, completed: 28 },
      { purpose: 'training', sorties: 200, hours: 300, completed: 92 },
    ],
    validationIssues: [],
  },
  {
    id: 'ap-2027-921',
    unitId: 'u-trd-921',
    unitShort: '921',
    status: 'pending-quan-chung',
    title: 'Kế hoạch bay năm 2027 — Trung đoàn KQ 921 (dự thảo)',
    description:
      'Dự thảo kế hoạch bay 2027 của Trung đoàn 921 — đang chờ Quân chủng phê duyệt cuối năm 2026.',
    preparedAt: '2026-09-12',
    aircraftAssigned: 18,
    pilotAssigned: 44,
    totalBudgetM: 92000,
    targets: [
      { purpose: 'training', sorties: 520, hours: 780, completed: 0 },
      { purpose: 'combat-readiness', sorties: 240, hours: 360, completed: 0 },
      { purpose: 'patrol', sorties: 140, hours: 250, completed: 0 },
      { purpose: 'test', sorties: 40, hours: 60, completed: 0 },
    ],
    validationIssues: [
      { level: 'error', category: 'aircraft', message: 'Thiếu 2 máy bay so với target sortie', detail: 'Cần 20 máy bay sẵn sàng để đạt 520 sortie HL/năm. Hiện có 18 (1 đại tu Q1).' },
      { level: 'warning', category: 'pilot', message: 'Chỉ tiêu HL Cấp 1 chưa đủ — cần thêm 4 phi công Cấp 1', detail: 'Hiện 12 Cấp 1 / cần 16 để chạy đủ chương trình huấn luyện chuyển loại.' },
      { level: 'info', category: 'budget', message: 'Ngân sách tăng 6.5% so với 2026', detail: '92,000 vs 86,400 triệu VND — phù hợp lạm phát + bổ sung khí tài.' },
    ],
  },
  {
    id: 'ap-2027-923',
    unitId: 'u-trd-923',
    unitShort: '923',
    status: 'submitted',
    title: 'Kế hoạch bay năm 2027 — Trung đoàn KQ 923 (dự thảo)',
    description: 'Dự thảo kế hoạch bay 2027 của Trung đoàn 923 — đang trình Sư đoàn xem xét.',
    preparedAt: '2026-09-20',
    aircraftAssigned: 14,
    pilotAssigned: 33,
    totalBudgetM: 70200,
    targets: [
      { purpose: 'training', sorties: 380, hours: 570, completed: 0 },
      { purpose: 'combat-readiness', sorties: 200, hours: 300, completed: 0 },
      { purpose: 'reconnaissance', sorties: 70, hours: 130, completed: 0 },
    ],
    validationIssues: [
      { level: 'warning', category: 'civil-coord', message: 'Cần ký lại hiệp đồng VATM ACC HCM (hết hạn 31/12/2026)', detail: 'Đường bay tactical RT-S04 đi qua không phận ACC HCM' },
    ],
  },
  {
    id: 'ap-2025-921',
    unitId: 'u-trd-921',
    unitShort: '921',
    status: 'completed',
    title: 'Kế hoạch bay năm 2025 — Trung đoàn KQ 921',
    description: 'Kế hoạch năm 2025 đã hoàn thành — đạt 96% target tổng sortie.',
    preparedAt: '2024-09-10',
    approvedAt: '2024-12-18',
    aircraftAssigned: 17,
    pilotAssigned: 40,
    totalBudgetM: 78000,
    targets: [
      { purpose: 'training', sorties: 460, hours: 690, completed: 442 },
      { purpose: 'combat-readiness', sorties: 220, hours: 330, completed: 218 },
      { purpose: 'patrol', sorties: 100, hours: 180, completed: 96 },
      { purpose: 'test', sorties: 30, hours: 45, completed: 28 },
    ],
    validationIssues: [],
  },
]

function generate(): AnnualFlightPlan[] {
  return SEEDS.map((s) => {
    const targets: AnnualPlanTarget[] = s.targets.map((t) => ({
      purpose: t.purpose,
      targetSorties: t.sorties,
      targetHours: t.hours,
      completedSorties: t.completed,
      completedHours: Math.round(t.hours * (t.completed / t.sorties || 0)),
    }))
    const year = parseInt(s.id.split('-')[1], 10)
    return {
      id: s.id,
      code: `KHB-NAM-${year}-${s.unitShort}`,
      year,
      unitId: s.unitId,
      title: s.title,
      description: s.description,
      preparedYear: year - 1,
      preparedAt: s.preparedAt,
      approvedAt: s.approvedAt,
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
      targets,
      totalTargetSorties: targets.reduce((sum, t) => sum + t.targetSorties, 0),
      totalTargetHours: targets.reduce((sum, t) => sum + t.targetHours, 0),
      totalBudgetM: s.totalBudgetM,
      aircraftAssigned: s.aircraftAssigned,
      pilotAssigned: s.pilotAssigned,
      status: s.status,
      approvalChain: buildApprovalChain(s.status),
      validationIssues: s.validationIssues,
      createdBy: 'Đại uý Nguyễn Hữu Nam',
      createdAt: s.preparedAt + 'T08:00:00',
      notes: s.notes,
    }
  })
}

export const ANNUAL_PLANS: AnnualFlightPlan[] = generate()
export const ANNUAL_PLANS_BY_ID: Record<string, AnnualFlightPlan> = Object.fromEntries(
  ANNUAL_PLANS.map((p) => [p.id, p]),
)

export const ANNUAL_PLAN_STATUS_LABEL: Record<AnnualPlanStatus, string> = {
  draft: 'Bản nháp',
  submitted: 'Đã trình',
  'pending-su-doan': 'Chờ Sư đoàn duyệt',
  'pending-quan-chung': 'Chờ Quân chủng duyệt',
  approved: 'Đã phê duyệt',
  'in-execution': 'Đang thực hiện',
  completed: 'Đã hoàn thành',
  rejected: 'Bị từ chối',
  cancelled: 'Đã huỷ',
}

export const ANNUAL_PLAN_STATUS_COLOR: Record<AnnualPlanStatus, string> = {
  draft: 'default',
  submitted: 'blue',
  'pending-su-doan': 'gold',
  'pending-quan-chung': 'gold',
  approved: 'green',
  'in-execution': 'cyan',
  completed: 'success',
  rejected: 'red',
  cancelled: 'default',
}

export const ANNUAL_PURPOSE_LABEL = PURPOSE_LABEL
